import { identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { useEffect, useState } from 'react';

export interface PortalIdentity {
  loading: boolean;
  displayName: string;
  email?: string;
  picture?: string;
  userEntityRef?: string;
  ownershipEntityRefs: string[];
}

const EMPTY: PortalIdentity = {
  loading: true,
  displayName: '',
  ownershipEntityRefs: [],
};

/**
 * Profile and Backstage identity of the signed-in user.
 *
 * Reads `identityApiRef` directly rather than `useUserProfile` from
 * `@backstage/plugin-user-settings` so the settings page does not pull that
 * plugin's Material-UI component tree into the bundle (ADR-0012).
 */
export const useUserIdentity = (): PortalIdentity => {
  const identityApi = useApi(identityApiRef);
  const [identity, setIdentity] = useState<PortalIdentity>(EMPTY);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const [profile, backstageIdentity] = await Promise.all([
        identityApi.getProfileInfo(),
        identityApi.getBackstageIdentity(),
      ]);
      if (cancelled) return;

      setIdentity({
        loading: false,
        displayName:
          profile.displayName ??
          profile.email ??
          backstageIdentity.userEntityRef,
        email: profile.email,
        picture: profile.picture,
        userEntityRef: backstageIdentity.userEntityRef,
        ownershipEntityRefs: backstageIdentity.ownershipEntityRefs ?? [],
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  return identity;
};

/** Initials for the avatar fallback, e.g. "Ariful Islam" -> "AI". */
export const initialsOf = (displayName: string): string =>
  displayName
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('') || '?';
